import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _map from "lodash/map";
import _set from "lodash/set";
import _isFunction from "lodash/isFunction";
import _get from "lodash/get";
import _isArray from "lodash/isArray";
import "regenerator-runtime/runtime.js";
import "core-js/modules/es6.set.js";
import "core-js/modules/es6.string.iterator.js";
import "core-js/modules/es6.object.to-string.js";
import "core-js/modules/es6.array.iterator.js";
import "core-js/modules/web.dom.iterable.js";
import "core-js/modules/es6.promise.js";
import "core-js/modules/es6.array.map.js";
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import createContextStore, { propType as contextStorePropType } from './context-store';
import reactUniversalComponent from '../lib/universal';
import defer from '../lib/defer';
import { toArray } from '../lib/tree-utils';
import getDisplayName from '../lib/get-display-name';
export default function createComponentsStore(_ref) {
  var components = _ref.components,
      _ref$contextStore = _ref.contextStore,
      contextStore = _ref$contextStore === void 0 ? createContextStore() : _ref$contextStore;
  var store = contextStore;
  if (!components) throw new Error('options.components required to createComponentsStore(options)'); // eslint-disable-line prettier/prettier
  // an object to store deferred objects
  // that keep track of when components are in a loading state

  var loadingSet = new Set();

  function loadAsyncComponent(asyncImport) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    // asyncImport is either `import('...')` or `[import(...), exportName]`
    if (_isArray(asyncImport)) return loadAsyncComponent(asyncImport[0], _objectSpread(_objectSpread({}, options), {}, {
      exportName: asyncImport[1]
    })); // track loading for ssr extraction

    var deferred = defer();
    loadingSet.add(deferred);

    var _options$exportName = options.exportName,
        exportName = _options$exportName === void 0 ? 'default' : _options$exportName,
        _options$onLoad = options.onLoad,
        _onLoad = _options$onLoad === void 0 ? function () {} : _options$onLoad;

    var UniversalComponent = reactUniversalComponent(asyncImport, {
      loading: function loading() {
        return null;
      },
      key: exportName,
      // attach Component statics to UniversalComponent and finally call onLoad
      onLoad: function onLoad(module) {
        // hoist Component's react statics required for dataProviders (dataDependencies, defaultProps, propTypes)
        //  Universal calls hoist(), but just at the wrong time.
        // and hoist propTypes and defaultProps for dataProviders
        var Component = module[exportName]; // hoist non-react statics (dataDependencies, Providers, etc...)

        hoistNonReactStatics(UniversalComponent, Component); // hoist Component's react-statics required for dataProviders

        UniversalComponent.propTypes = Component.propTypes;
        UniversalComponent.defaultProps = Component.defaultProps; // pretty display name

        UniversalComponent.displayName = "UniversalComponent(".concat(getDisplayName(Component), ")"); // eslint-disable-line prettier/prettier
        // attach loaded component to UniversalComponent (for tests)

        UniversalComponent.Component = Component; // mark as no longer codesplit (async)

        UniversalComponent.isCodesplit = false; // call onload with the universal component with attached statics

        _onLoad(UniversalComponent);

        loadingSet.delete(deferred);
        deferred.resolve();
      }
    }); // mark this component as code-split for loadComponentToStore

    UniversalComponent.isCodesplit = true; // start loading the promised module immediately (specific to react-universal-component)

    UniversalComponent.preload();
    return UniversalComponent;
  } // load a component and wrap it with data-dependencies HOC when it is ready


  function loadComponent(type) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var _options$onLoad2 = options.onLoad,
        onLoad = _options$onLoad2 === void 0 ? function () {} : _options$onLoad2;

    var definition = _get(components, type);

    var Component = definition(); // check if code-split

    if (_isFunction(Component.then) || _isArray(Component)) {
      // return universal code-split component
      return loadAsyncComponent(Component, {
        onLoad: onLoad
      });
    } // not code-split, wrap already fully loaded Component and return


    return Component;
  } // stores onLoad handlers by `type`


  var onLoadHandlers = {}; // stores onLoad handler for giver type

  var registerOnLoadHandler = function registerOnLoadHandler(type, handler) {
    var handlers = _get(onLoadHandlers, type, []);

    handlers.push(handler);

    _set(onLoadHandlers, type, handlers);
  }; // calls all stored onload handlers when Component is finally loaded


  var flushOnLoadHandlers = function flushOnLoadHandlers(type, Component) {
    var handlers = _get(onLoadHandlers, type, []);

    delete onLoadHandlers[type];
    handlers.forEach(function (handler) {
      return handler(Component);
    });
  }; // load a component into store, and update it if code-split when it finally loads


  function loadComponentToStore(type) {
    var Component = loadComponent(type, {
      onLoad: function onLoad(LoadedComponent) {
        // code-split components call onLoad after universal component loads
        store.set(type, LoadedComponent);
        flushOnLoadHandlers(type, LoadedComponent);
      }
    });
    store.set(type, Component);
    return Component;
  } // return a Component for type from the store


  function load(type) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var _options$onLoad3 = options.onLoad,
        onLoad = _options$onLoad3 === void 0 ? function () {} : _options$onLoad3; // always prefer component in store

    var Component = store.get(type) || loadComponentToStore(type, {
      onLoad: onLoad
    });

    if (Component.isCodesplit === true) {
      // codesplit components have only one universal handler,
      // so side-register the callback to ensure it gets called
      // when the single `UniversalComponent` instance for this
      // type is finally loaded
      registerOnLoadHandler(type, onLoad);
    } else {
      // non code-split components call onload immediately
      onLoad(Component);
    }

    return Component;
  } // return map of { [type]: Component } for unique node-types in schema


  var loadTree = function loadTree(node, schema) {
    return toArray(node, schema).reduce(function (memo, _ref2) {
      var type = _ref2.type;
      if (_get(memo, type)) return memo;

      _set(memo, type, load(type));

      return memo;
    }, {});
  }; // promise resolves when component is fully loaded, code-split or not


  var preloadComponent = function preloadComponent(Component) {
    if (_isFunction(Component.preload)) return Component.preload();
    return Promise.resolve(Component);
  }; // preload all components in list or map of Components


  var preload = function preload(treeComponents) {
    return Promise.all(_map(treeComponents, preloadComponent)).then(function () {
      return store.get();
    });
  }; // preload all types of components found in node-tree


  var preloadTree = function preloadTree(node, schema) {
    return preload(loadTree(node, schema));
  };

  function hasPending() {
    return loadingSet.size > 0;
  }

  function waitForPending() {
    return _waitForPending.apply(this, arguments);
  }

  function _waitForPending() {
    _waitForPending = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee() {
      return regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return Promise.all(_toConsumableArray(loadingSet).map(function (_ref3) {
                var promise = _ref3.promise;
                return promise;
              }));

            case 2:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));
    return _waitForPending.apply(this, arguments);
  }

  return {
    load: load,
    loadTree: loadTree,
    preload: preload,
    preloadTree: preloadTree,
    hasPending: hasPending,
    waitForPending: waitForPending,
    // for listening to changes
    store: store
  };
}
export var propType = PropTypes.shape({
  load: PropTypes.func.isRequired,
  loadTree: PropTypes.func.isRequired,
  preload: PropTypes.func.isRequired,
  preloadTree: PropTypes.func.isRequired,
  hasPending: PropTypes.func.isRequired,
  waitForPending: PropTypes.func.isRequired,
  store: contextStorePropType.isRequired
});